import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { MaskedLines, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";

const HERO_IMG = "https://lasersonly.com/cdn/shop/articles/laser_wood_engraving_process.png?v=1701371829";
const STORY_IMG = "https://i.pinimg.com/originals/36/6f/b1/366fb1ba1d721ac658795dcd59f28034.jpg";

const chapters = [
  { n: "01", t: "Une seule paire de mains", d: "Chaque pièce est imaginée et réalisée par une seule personne, dans un petit atelier. Pas de chaîne, pas d'usine — juste de la patience et le souci du détail." },
  { n: "02", t: "Gravure laser & sublimation", d: "Deux techniques, un même soin. La gravure laser marque durablement le bois et le métal ; la sublimation fait entrer l'encre dans la matière. On choisit la bonne méthode pour chaque objet." },
  { n: "03", t: "Votre histoire, gravée", d: "Un prénom, une photo, une date. Vous apportez le sens, je m'occupe de le rendre durable et beau — au laser ou à la presse." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [gallery, setGallery] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.customizable).slice(0, 4))).catch(() => {});
    api.get("/products?in_realisations=true").then((r) => setGallery(r.data.slice(0, 4))).catch(() => {});
    api.get("/products?featured_home=true").then((r) => {
      const grav = r.data.filter((p) => p.technique === "Gravure laser").slice(0, 2);
      const subl = r.data.filter((p) => p.technique === "Sublimation").slice(0, 2);
      setFeatured([...grav, ...subl]);
    }).catch(() => {});
  }, []);

  const featuredList = featured.length ? featured : products;

  return (
    <div>
      <SEO path="/" />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] overflow-hidden border-b border-line">
        <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Atelier o-crea" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-cream/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-cream/95 via-cream/65 to-cream/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-cream via-transparent to-transparent" />
        </motion.div>

        <motion.div style={{ y: yText }} className="relative z-10 mx-auto flex max-w-[1400px] flex-col justify-center px-5 pt-28 pb-20 md:min-h-[92vh] md:px-10">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-ink2"
          >
            <span className="h-px w-10 bg-terra" /> Fait maison · Vaud, Suisse
          </motion.p>

          <h1 className="font-serif text-5xl font-light leading-[0.95] tracking-tight text-ink sm:text-7xl lg:text-8xl">
            <MaskedLines lines={["Des objets", "qui vous"]} delay={0.25} />
            <span className="block">
              <MaskedLines lines={["ressemblent."]} delay={0.5} className="italic text-terra" />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-md text-base leading-relaxed text-ink2 md:text-lg"
          >
            Gravure laser sur bois et métal, et sublimation sur mugs, gourdes et sacs. Choisissez un concept ou créez le vôtre, mot à mot, image à image.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/catalogue" data-testid="hero-cta-catalogue"
              className="group flex items-center gap-2 bg-ink px-8 py-4 text-sm uppercase tracking-widest text-cream transition-colors hover:bg-terra active:scale-95"
            >
              Voir le catalogue
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/atelier" data-testid="hero-cta-atelier"
              className="link-underline text-sm uppercase tracking-widest text-ink"
            >
              Découvrir l'atelier
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={["Gravure Laser", "Sublimation", "Bois & Métal", "Fait Maison", "Pièce Unique"]} />

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-36">
        <Reveal>
          <p className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-terra">
            <Sparkles className="h-4 w-4" /> Le manifeste
          </p>
          <h2 className="max-w-3xl font-serif text-4xl font-light leading-tight tracking-tight text-ink md:text-6xl">
            Ce n'est pas une boutique. C'est un atelier avec une histoire.
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-4 md:grid-cols-12">
          <Reveal className="md:col-span-7" delay={0.05}>
            <div className="relative h-full overflow-hidden border border-line">
              <img src={STORY_IMG} alt="Matériaux faits main" className="h-full min-h-[320px] w-full object-cover" />
            </div>
          </Reveal>
          <div className="grid gap-4 md:col-span-5">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={0.1 + i * 0.08}>
                <div className="border border-line bg-cream p-8 transition-colors hover:bg-sand">
                  <div className="flex items-baseline gap-4">
                    <span className="font-serif text-3xl font-light text-terra">{c.n}</span>
                    <h3 className="font-serif text-2xl font-medium text-ink">{c.t}</h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-ink2">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="border-t border-line bg-sand/60 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="mb-14 flex items-end justify-between">
            <Reveal>
              <p className="mb-3 text-xs uppercase tracking-[0.3em] text-ink3">À la une · Gravure laser & sublimation</p>
              <h2 className="font-serif text-4xl font-light tracking-tight text-ink md:text-6xl" data-testid="home-featured-title">Des objets à personnaliser</h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-ink2" data-testid="home-featured-subtitle">
                Choisissez une pièce, ajoutez votre texte, votre photo ou votre prénom et visualisez le rendu en direct — je la réalise ensuite à la main, rien que pour vous.
              </p>
            </Reveal>
            <Link to="/catalogue" className="link-underline hidden text-sm uppercase tracking-widest text-ink md:block">
              Tout voir →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            {featuredList.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* REALISATIONS TEASER */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <div className="mb-12 flex items-end justify-between">
          <Reveal>
            <p className="mb-3 text-xs uppercase tracking-[0.3em] text-terra">Déjà fait</p>
            <h2 className="font-serif text-4xl font-light tracking-tight text-ink md:text-6xl">Nos produits</h2>
          </Reveal>
          <Link to="/produits" data-testid="home-realisations-link" className="link-underline hidden text-sm uppercase tracking-widest text-ink md:block">
            Voir les produits →
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {gallery.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.08}>
              <Link to={`/produit/${r.id}`} className="group block overflow-hidden border border-line bg-sand">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={r.image_url} alt={r.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                </div>
                <div className="flex items-center justify-between px-3 py-3">
                  <span className="font-serif text-sm text-ink">{r.name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-ink3">{r.category}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <Link to="/produits" className="link-underline mt-8 inline-block text-sm uppercase tracking-widest text-ink md:hidden">
          Voir les produits →
        </Link>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 py-28 md:px-10 md:py-40">
        <Reveal className="border border-line bg-ink p-10 text-cream md:p-20">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight md:text-6xl">
              Une idée en tête ? Donnons-lui une forme.
            </h2>
            <Link
              to="/catalogue" data-testid="cta-create"
              className="group flex shrink-0 items-center gap-2 border border-cream px-8 py-4 text-sm uppercase tracking-widest transition-colors hover:bg-cream hover:text-ink active:scale-95"
            >
              Commencer
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
